import type { MarkerFeature } from "../../../../../utils/models/MarkerFeature";
import type { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { UndoCommand, CommandContext } from "../types";

/**
 * Command for adding a marker at the end of a route.
 * Stores just the marker and the associated linestring (~50KB).
 */
export class AddMarkerCommand implements UndoCommand {
  readonly type = 'ADD_MARKER';
  readonly routeKey: string;

  private markerIndex: number;
  private marker: MarkerFeature;
  private lineString: LineStringFeature | null;

  constructor(data: {
    routeKey: string;
    markerIndex: number;
    marker: MarkerFeature;
    lineString: LineStringFeature | null;
  }) {
    this.routeKey = data.routeKey;
    this.markerIndex = data.markerIndex;
    this.marker = JSON.parse(JSON.stringify(data.marker));
    this.lineString = data.lineString ? JSON.parse(JSON.stringify(data.lineString)) : null;
  }

  undo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    // Remove the marker
    route.markers.splice(this.markerIndex, 1);

    // Remove the associated linestring if one was created
    if (this.lineString !== null && route.lineStrings.length > 0) {
      route.lineStrings.splice(route.lineStrings.length - 1, 1);
    }

    route.updated_at = new Date();
  }

  redo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    // Re-add the marker
    route.markers.splice(this.markerIndex, 0, JSON.parse(JSON.stringify(this.marker)));

    // Re-add the linestring
    if (this.lineString !== null) {
      route.lineStrings.push(JSON.parse(JSON.stringify(this.lineString)));
    }

    route.updated_at = new Date();
  }

  updateProvisionalLine(provisionalId: string, realLine: LineStringFeature): boolean {
    if (
      this.lineString &&
      this.lineString.lineStringProperties?.id === provisionalId
    ) {
      this.lineString = JSON.parse(JSON.stringify(realLine));
      return true;
    }
    return false;
  }
}
